import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BadgeCertifie from "../../components/BadgeCertifie";

const API = "https://live-evo.com/api";
const BASE = "https://live-evo.com";

const linkButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  color: "#00f2ff",
  background: "rgba(0,242,255,.07)",
  border: "1px solid rgba(0,242,255,.25)",
  borderRadius: 9,
  padding: "7px 10px",
  fontSize: 10,
  fontWeight: 900,
};

const REFUSAL_REASONS = [
  {
    id: "commercial",
    icon: "©",
    title: "Œuvre commerciale reconnue",
    detail:
      "Le son correspond à une œuvre commerciale reconnue.",
    reason:
      "Œuvre commerciale reconnue — droits/licence non démontrés.",
  },
  {
    id: "rights",
    icon: "📄",
    title: "Droits / licence non démontrés",
    detail:
      "Aucune preuve de licence ou d'autorisation suffisante n'a été fournie.",
    reason:
      "Droits ou licence nécessaires à la publication non démontrés.",
  },
  {
    id: "metadata",
    icon: "👤",
    title: "Titre ou artiste déclaré incohérent",
    detail:
      "Les informations déclarées ne correspondent pas au contenu identifié.",
    reason:
      "Les informations déclarées pour le titre ou l'artiste sont incohérentes avec le contenu audio.",
  },
  {
    id: "duplicate",
    icon: "▣",
    title: "Doublon / copie détectée",
    detail:
      "Ce son est déjà présent ou correspond à un contenu existant sur EVO-LIVE.",
    reason:
      "Doublon ou copie détectée — ce contenu est déjà présent sur EVO-LIVE.",
  },
  {
    id: "quality",
    icon: "♫",
    title: "Qualité audio insuffisante",
    detail:
      "Bruit important, coupures ou qualité insuffisante pour le catalogue.",
    reason:
      "Qualité audio insuffisante pour une publication dans le catalogue EVO-LIVE.",
  },
  {
    id: "invalid",
    icon: "⚠",
    title: "Fichier incomplet ou invalide",
    detail:
      "Le fichier est incomplet, corrompu ou inutilisable.",
    reason:
      "Fichier audio incomplet, invalide ou inutilisable.",
  },
  {
    id: "other",
    icon: "•••",
    title: "Autre motif",
    detail:
      "Saisir un motif personnalisé dans la précision ci-dessous.",
    reason: "",
  },
];

function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function AdminMusicValidationPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReasonKey, setRejectReasonKey] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [rejectError, setRejectError] = useState("");

  const token = localStorage.getItem("evoAccessToken");

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    }),
    [token]
  );

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API}/music/staff-reports`, {
        headers: {
          Authorization: `Bearer ${token || ""}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Erreur chargement");
      }

      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Impossible de charger les sons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (item) => {
    if (
      !window.confirm(
        `Valider "${item.title}" et l'ajouter au catalogue musique EVO ?`
      )
    ) {
      return;
    }

    setWorkingId(item.id);

    try {
      const res = await fetch(
        `${API}/music/staff-reports/${item.id}/approve`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({}),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Validation impossible");
      }

      alert("✅ Son validé et ajouté au catalogue.");
      await load();
    } catch (e) {
      alert(e.message || "Erreur de validation.");
    } finally {
      setWorkingId(null);
    }
  };

  const openRejectModal = (item) => {
    const isCommercial =
      item.detection_provider === "audd" &&
      Number(item.detected_match) === 1 &&
      Boolean(
        item.detected_title ||
        item.detected_artist
      );

    const isDuplicate =
      item.detection_provider === "local" &&
      Number(item.detected_match) === 1;

    setRejectItem(item);

    setRejectReasonKey(
      isCommercial
        ? "commercial"
        : isDuplicate
        ? "duplicate"
        : ""
    );

    setRejectNote("");
    setRejectError("");
  };

  const closeRejectModal = () => {
    if (workingId) return;

    setRejectItem(null);
    setRejectReasonKey("");
    setRejectNote("");
    setRejectError("");
  };

  const confirmReject = async () => {
    if (!rejectItem) return;

    const selected =
      REFUSAL_REASONS.find(
        (reason) => reason.id === rejectReasonKey
      ) || null;

    if (!selected) {
      setRejectError("Choisis un motif de refus.");
      return;
    }

    const note = String(rejectNote || "").trim();

    if (selected.id === "other" && !note) {
      setRejectError(
        "Précise le motif du refus pour utiliser « Autre motif »."
      );
      return;
    }

    let finalReason =
      selected.id === "other"
        ? note
        : selected.reason;

    if (
      selected.id !== "other" &&
      note
    ) {
      finalReason += ` Précision : ${note}`;
    }

    finalReason = finalReason
      .trim()
      .slice(0, 500);

    setRejectError("");
    setWorkingId(rejectItem.id);

    try {
      const res = await fetch(
        `${API}/music/staff-reports/${rejectItem.id}/reject`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            reason: finalReason,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error || "Refus impossible"
        );
      }

      setRejectItem(null);
      setRejectReasonKey("");
      setRejectNote("");
      setRejectError("");

      await load();
    } catch (e) {
      setRejectError(
        e.message || "Erreur de refus."
      );
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#02070d",
        color: "#fff",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <button
          onClick={() => navigate("/admin")}
          style={backButton}
        >
          ← Retour panneau admin
        </button>

        <div style={{ marginTop: 24, marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 26 }}>
            🎧 Sons à valider
          </h1>

          <div
            style={{
              color: "#7d91a0",
              marginTop: 6,
              fontSize: 13,
            }}
          >
            Sons envoyés par les utilisateurs depuis l'application EVO-LIVE.
          </div>
        </div>

        {loading ? (
          <div style={emptyBox}>Chargement...</div>
        ) : error ? (
          <div
            style={{
              ...emptyBox,
              color: "#ff5577",
              borderColor: "rgba(255,0,64,.4)",
            }}
          >
            <div>{error}</div>

            {/session expirée|reconnecte-toi/i.test(error) && (
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem("evoAccessToken");
                  navigate("/login");
                }}
                style={{
                  marginTop: 18,
                  padding: "11px 18px",
                  borderRadius: 10,
                  border: "1px solid rgba(0,242,255,.35)",
                  background: "rgba(0,242,255,.10)",
                  color: "#00f2ff",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                🔐 SE RECONNECTER
              </button>
            )}
          </div>
        ) : items.length === 0 ? (
          <div style={emptyBox}>
            ✅ Aucun son en attente de validation.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 14,
            }}
          >
            {items.map((item) => {
              const score = Number(item.detection_score || 0);
              const hasMatch = Number(item.detected_match) === 1;
              const busy = workingId === item.id;

              let detection = {};

              try {
                detection =
                  typeof item.detection_response === "string"
                    ? JSON.parse(item.detection_response || "{}")
                    : item.detection_response || {};
              } catch {
                detection = {};
              }

              const commercial =
                detection?.commercial || {};

              const local =
                detection?.local || {};

              const attempts =
                Array.isArray(commercial?.attempts)
                  ? commercial.attempts
                  : [];

              const localMatches =
                Array.isArray(local?.matches)
                  ? local.matches
                  : [];

              const commercialRecognized =
                commercial?.recognized === true ||
                (
                  item.detection_provider === "audd" &&
                  hasMatch &&
                  Boolean(
                    item.detected_title ||
                    item.detected_artist
                  )
                );

              const commercialChecked =
                commercial?.checked === true;

              const commercialError =
                commercial?.error || null;

              const localHasMatch =
                localMatches.length > 0 ||
                (
                  item.detection_provider === "local" &&
                  hasMatch
                );

              const normalizeValue = (value) =>
                String(value || "")
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, " ")
                  .trim();

              const titleDiffers =
                commercialRecognized &&
                item.detected_title &&
                normalizeValue(item.title) !==
                  normalizeValue(item.detected_title);

              const artistDiffers =
                commercialRecognized &&
                item.detected_artist &&
                (
                  !item.claimed_artist ||
                  normalizeValue(item.claimed_artist) !==
                    normalizeValue(item.detected_artist)
                );

              const declarationsDiffer =
                Boolean(titleDiffers || artistDiffers);

              const appleMusicUrl =
                commercial?.apple_music?.url ||
                null;

              const spotifyUrl =
                commercial?.spotify
                  ?.external_urls?.spotify ||
                null;

              const deezerUrl =
                commercial?.deezer?.link ||
                null;

              const searchText = [
                item.detected_artist,
                item.detected_title,
                item.detected_isrc,
              ]
                .filter(Boolean)
                .join(" ");

              const webSearchUrl =
                searchText
                  ? `https://www.google.com/search?q=${encodeURIComponent(
                      searchText
                    )}`
                  : null;

              return (
                <div key={item.id} style={card}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "70px minmax(200px,1fr) minmax(260px,1.2fr) 46px",
                      gap: 18,
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        position: "relative",
                      }}
                    >
                      {item.cover_path ? (
                        <>
                          <img
                            src={mediaUrl(item.cover_path)}
                            alt=""
                            style={{
                              width: 64,
                              height: 64,
                              objectFit: "cover",
                              borderRadius: 12,
                              display: "block",
                            }}
                          />

                          {Number(item.is_verified) === 1 &&
                            Number(item.cover_is_profile_picture) === 1 && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: -4,
                                  bottom: -4,
                                  zIndex: 2,
                                }}
                              >
                                <BadgeCertifie
                                  isValidated={true}
                                  compact={true}
                                />
                              </div>
                            )}
                        </>
                      ) : (
                        <div style={coverPlaceholder}>🎵</div>
                      )}
                    </div>

                    <div>
                      <div
                        style={{
                          color: "#00f2ff",
                          fontSize: 10,
                          fontWeight: 900,
                        }}
                      >
                        ENVOI #{item.id}
                      </div>

                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 900,
                          marginTop: 4,
                        }}
                      >
                        {item.title}
                      </div>

                      <div
                        style={{
                          color: "#9aabb7",
                          fontSize: 12,
                          marginTop: 5,
                        }}
                      >
                        👤 {item.pseudo}
                      </div>

                      <div
                        style={{
                          color: "#718593",
                          fontSize: 11,
                          marginTop: 3,
                        }}
                      >
                        {item.claimed_artist || "Artiste non renseigné"}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          ...statusBox,
                          borderColor:
                            commercialRecognized ||
                            localHasMatch
                              ? "rgba(255,66,96,.55)"
                              : commercialError
                              ? "rgba(255,180,0,.55)"
                              : commercialChecked
                              ? "rgba(255,180,0,.40)"
                              : "rgba(0,242,255,.30)",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 900,
                            color:
                              commercialRecognized ||
                              localHasMatch
                                ? "#ff526f"
                                : "#ffc857",
                          }}
                        >
                          {commercialRecognized
                            ? "🔴 ŒUVRE COMMERCIALE RECONNUE"
                            : localHasMatch
                            ? `⚠️ Correspondance interne EVO : ${score.toFixed(1)} %`
                            : commercialError
                            ? "⚠️ Analyse commerciale incomplète"
                            : commercialChecked
                            ? "🟠 Aucune œuvre commerciale reconnue"
                            : "⏳ Analyse en attente"}
                        </div>

                        <div
                          style={{
                            color: "#899ba8",
                            fontSize: 10,
                            marginTop: 5,
                          }}
                        >
                          Analyse :{" "}
                          {item.detection_provider || "en attente"}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 8,
                          color: item.rights_confirmed
                            ? "#30e39b"
                            : "#ff526f",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {item.rights_confirmed
                          ? "✅ Droits déclarés par l'utilisateur"
                          : "❌ Droits non confirmés"}
                      </div>
                    </div>

                    <button
                      type="button"
                      aria-label={
                        expandedId === item.id
                          ? "Fermer le dossier"
                          : "Ouvrir le dossier"
                      }
                      aria-expanded={expandedId === item.id}
                      onClick={() =>
                        setExpandedId((current) =>
                          current === item.id ? null : item.id
                        )
                      }
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 12,
                        border: "1px solid rgba(0,242,255,.28)",
                        background:
                          expandedId === item.id
                            ? "rgba(0,242,255,.12)"
                            : "rgba(255,255,255,.035)",
                        color: "#00f2ff",
                        cursor: "pointer",
                        fontSize: 19,
                        fontWeight: 900,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {expandedId === item.id ? "▲" : "▼"}
                    </button>
                  </div>

                  <div
                    style={{
                      display:
                        expandedId === item.id ? "block" : "none",
                      marginTop: 18,
                      padding: 16,
                      borderRadius: 14,
                      border:
                        "1px solid rgba(0,242,255,.16)",
                      background:
                        "rgba(0,0,0,.20)",
                    }}
                  >
                    <div
                      style={{
                        color: "#00f2ff",
                        fontSize: 12,
                        fontWeight: 900,
                        marginBottom: 14,
                      }}
                    >
                      🔎 DOSSIER DE DÉTECTION
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit,minmax(230px,1fr))",
                        gap: 14,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "#91a4b2",
                            fontSize: 10,
                            fontWeight: 900,
                            marginBottom: 7,
                          }}
                        >
                          INFORMATIONS DÉCLARÉES
                        </div>

                        <div style={{fontSize: 12}}>
                          <b>Titre :</b>{" "}
                          {item.title || "Non renseigné"}
                        </div>

                        <div style={{fontSize: 12, marginTop: 5}}>
                          <b>Artiste :</b>{" "}
                          {item.claimed_artist ||
                            "Non renseigné"}
                        </div>

                        <div style={{fontSize: 12, marginTop: 5}}>
                          <b>Utilisateur :</b>{" "}
                          {item.pseudo}
                        </div>

                        <div style={{fontSize: 12, marginTop: 5}}>
                          <b>Déclaration droits :</b>{" "}
                          {item.rights_confirmed
                            ? "Oui"
                            : "Non"}
                        </div>
                      </div>

                      <div>
                        <div
                          style={{
                            color: commercialRecognized
                              ? "#ff526f"
                              : "#ffc857",
                            fontSize: 10,
                            fontWeight: 900,
                            marginBottom: 7,
                          }}
                        >
                          RECONNAISSANCE AUDD
                        </div>

                        {commercialRecognized ? (
                          <>
                            <div style={{fontSize: 12}}>
                              <b>Titre :</b>{" "}
                              {item.detected_title ||
                                commercial.title ||
                                "Non fourni"}
                            </div>

                            <div style={{fontSize: 12, marginTop: 5}}>
                              <b>Artiste :</b>{" "}
                              {item.detected_artist ||
                                commercial.artist ||
                                "Non fourni"}
                            </div>

                            <div style={{fontSize: 12, marginTop: 5}}>
                              <b>Album :</b>{" "}
                              {commercial.album ||
                                "Non fourni"}
                            </div>

                            <div style={{fontSize: 12, marginTop: 5}}>
                              <b>Label :</b>{" "}
                              {commercial.label ||
                                "Non fourni"}
                            </div>

                            <div style={{fontSize: 12, marginTop: 5}}>
                              <b>Date de sortie :</b>{" "}
                              {commercial.release_date ||
                                "Non fournie"}
                            </div>

                            <div style={{fontSize: 12, marginTop: 5}}>
                              <b>ISRC :</b>{" "}
                              {item.detected_isrc ||
                                commercial.isrc ||
                                "Non fourni"}
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              color: "#b6c0c7",
                              fontSize: 12,
                            }}
                          >
                            {commercialError
                              ? `Erreur : ${commercialError}`
                              : commercialChecked
                              ? "Aucun morceau reconnu sur les passages analysés."
                              : "Analyse commerciale non terminée."}
                          </div>
                        )}
                      </div>

                      <div>
                        <div
                          style={{
                            color: "#91a4b2",
                            fontSize: 10,
                            fontWeight: 900,
                            marginBottom: 7,
                          }}
                        >
                          ANALYSE TECHNIQUE
                        </div>

                        <div style={{fontSize: 12}}>
                          <b>Chromaprint EVO :</b>{" "}
                          {localHasMatch
                            ? `${localMatches.length || 1} correspondance(s)`
                            : "Aucune copie interne trouvée"}
                        </div>

                        <div style={{fontSize: 12, marginTop: 5}}>
                          <b>Passages AudD :</b>{" "}
                          {attempts.length || 0}
                        </div>

                        {attempts.map((attempt, index) => (
                          <div
                            key={`${item.id}-attempt-${index}`}
                            style={{
                              marginTop: 4,
                              fontSize: 11,
                              color: attempt.recognized
                                ? "#30e39b"
                                : "#91a4b2",
                            }}
                          >
                            Passage{" "}
                            {attempt.passage || index + 1}
                            {" — "}
                            {Number.isFinite(
                              Number(attempt.start_seconds)
                            )
                              ? `${Number(
                                  attempt.start_seconds
                                ).toFixed(1)} s`
                              : "position inconnue"}
                            {" — "}
                            {attempt.recognized
                              ? "✅ reconnu"
                              : "❌ non reconnu"}
                          </div>
                        ))}

                        {localMatches.map((match, index) => (
                          <div
                            key={`${item.id}-local-${index}`}
                            style={{
                              marginTop: 5,
                              fontSize: 11,
                              color: "#ffb6c2",
                            }}
                          >
                            EVO :{" "}
                            {match.matchedSourceType ||
                              "source"}{" "}
                            #{match.matchedSourceId || "?"}
                            {" — "}
                            {Number(
                              match.similarity || 0
                            ).toFixed(1)}
                            %
                          </div>
                        ))}
                      </div>
                    </div>

                    {declarationsDiffer && (
                      <div
                        style={{
                          marginTop: 14,
                          padding: 11,
                          borderRadius: 10,
                          background:
                            "rgba(255,66,96,.08)",
                          border:
                            "1px solid rgba(255,66,96,.28)",
                          color: "#ff8095",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        ⚠️ Les informations déclarées par
                        l'utilisateur ne correspondent pas à
                        l'œuvre reconnue.
                      </div>
                    )}

                    {commercialRecognized && (
                      <div
                        style={{
                          marginTop: 12,
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {commercial.song_link && (
                          <a
                            href={commercial.song_link}
                            target="_blank"
                            rel="noreferrer"
                            style={linkButtonStyle}
                          >
                            🔗 Correspondance AudD
                          </a>
                        )}

                        {appleMusicUrl && (
                          <a
                            href={appleMusicUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={linkButtonStyle}
                          >
                            Apple Music
                          </a>
                        )}

                        {spotifyUrl && (
                          <a
                            href={spotifyUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={linkButtonStyle}
                          >
                            Spotify
                          </a>
                        )}

                        {deezerUrl && (
                          <a
                            href={deezerUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={linkButtonStyle}
                          >
                            Deezer
                          </a>
                        )}

                        {webSearchUrl && (
                          <a
                            href={webSearchUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={linkButtonStyle}
                          >
                            🔎 Recherche web
                          </a>
                        )}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 14,
                        padding: 11,
                        borderRadius: 10,
                        background:
                          "rgba(255,190,40,.06)",
                        border:
                          "1px solid rgba(255,190,40,.20)",
                        color: "#cbbd8a",
                        fontSize: 10,
                        lineHeight: 1.5,
                      }}
                    >
                      ⚠️ Une reconnaissance audio identifie
                      une œuvre, mais ne prouve pas à elle
                      seule que l'utilisateur ne possède
                      aucune licence. Le label est une piste
                      de vérification et pas automatiquement
                      la preuve du titulaire actuel de tous
                      les droits.
                    </div>

                    {commercialRecognized && (
                      <div
                        style={{
                          marginTop: 10,
                          color: "#9cabb5",
                          fontSize: 10,
                        }}
                      >
                        Motif de modération possible si aucun
                        justificatif valide n'est fourni :
                        <b>
                          {" "}
                          œuvre commerciale reconnue —
                          droits/licence non démontrés.
                        </b>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display:
                        expandedId === item.id ? "block" : "none",
                      marginTop: 16,
                      paddingTop: 14,
                      borderTop: "1px solid rgba(255,255,255,.07)",
                    }}
                  >
                    <audio
                      controls
                      preload="metadata"
                      src={mediaUrl(item.audio_path)}
                      style={{
                        width: "100%",
                        maxWidth: 520,
                      }}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      display:
                        expandedId === item.id ? "flex" : "none",
                      gap: 9,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      disabled={busy || item.status === "analyzing"}
                      onClick={() => approve(item)}
                      style={{
                        ...actionButton,
                        background: "#0bbf72",
                        opacity:
                          busy || item.status === "analyzing" ? 0.5 : 1,
                      }}
                    >
                      ✅ VALIDER
                    </button>

                    <button
                      disabled={busy}
                      onClick={() => openRejectModal(item)}
                      style={{
                        ...actionButton,
                        background: "#d83252",
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      ❌ REFUSER
                    </button>

                    <div
                      style={{
                        marginLeft: "auto",
                        color: "#6e8190",
                        fontSize: 10,
                        alignSelf: "center",
                      }}
                    >
                      Statut : {item.status}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rejectItem && (
        <div
          role="presentation"
          onMouseDown={closeRejectModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,5,10,.82)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Refuser le son"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 620,
              maxHeight: "92vh",
              overflowY: "auto",
              background:
                "linear-gradient(180deg,#071522 0%,#03101a 100%)",
              border:
                "1px solid rgba(0,242,255,.65)",
              borderRadius: 20,
              padding: 22,
              boxShadow:
                "0 0 0 1px rgba(0,242,255,.08), 0 0 45px rgba(0,242,255,.16), 0 30px 80px rgba(0,0,0,.65)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  flex: "0 0 44px",
                  borderRadius: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ff4167",
                  border:
                    "2px solid rgba(255,65,103,.8)",
                  fontSize: 25,
                  fontWeight: 900,
                }}
              >
                ⊘
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    color: "#fff",
                    fontSize: 22,
                    fontWeight: 950,
                  }}
                >
                  Refuser le son
                </div>

                <div
                  style={{
                    color: "#8da2b1",
                    fontSize: 12,
                    marginTop: 5,
                  }}
                >
                  Choisis un motif de refus pour{" "}
                  <b style={{ color: "#dce9ef" }}>
                    "{rejectItem.title}"
                  </b>
                  .
                </div>
              </div>

              <button
                type="button"
                disabled={Boolean(workingId)}
                onClick={closeRejectModal}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#7eeeff",
                  fontSize: 23,
                  cursor: workingId
                    ? "default"
                    : "pointer",
                  padding: 2,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                marginTop: 20,
                display: "grid",
                gap: 9,
              }}
            >
              {REFUSAL_REASONS.map((reason) => {
                const selected =
                  rejectReasonKey === reason.id;

                return (
                  <button
                    key={reason.id}
                    type="button"
                    disabled={Boolean(workingId)}
                    onClick={() => {
                      setRejectReasonKey(reason.id);
                      setRejectError("");
                    }}
                    style={{
                      width: "100%",
                      appearance: "none",
                      WebkitAppearance: "none",
                      textAlign: "left",
                      display: "grid",
                      gridTemplateColumns:
                        "42px minmax(0,1fr) 24px",
                      gap: 12,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: 14,
                      border: selected
                        ? "1px solid #00f2ff"
                        : "1px solid rgba(122,157,180,.25)",
                      background: selected
                        ? "rgba(0,242,255,.09)"
                        : "rgba(255,255,255,.025)",
                      boxShadow: selected
                        ? "0 0 20px rgba(0,242,255,.10)"
                        : "none",
                      cursor: workingId
                        ? "default"
                        : "pointer",
                      color: "#fff",
                    }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          reason.id === "commercial"
                            ? "rgba(255,65,103,.09)"
                            : "rgba(0,242,255,.05)",
                        color:
                          reason.id === "commercial"
                            ? "#ff5274"
                            : "#9defff",
                        fontSize: 20,
                        fontWeight: 950,
                      }}
                    >
                      {reason.icon}
                    </div>

                    <div>
                      <div
                        style={{
                          color: selected
                            ? "#8ff7ff"
                            : "#f2f8fb",
                          fontSize: 13,
                          fontWeight: 900,
                        }}
                      >
                        {reason.title}
                      </div>

                      <div
                        style={{
                          color: "#8096a6",
                          fontSize: 10,
                          lineHeight: 1.45,
                          marginTop: 3,
                        }}
                      >
                        {reason.detail}
                      </div>
                    </div>

                    <div
                      style={{
                        width: 19,
                        height: 19,
                        borderRadius: 10,
                        border: selected
                          ? "5px solid #00dfff"
                          : "2px solid #66869e",
                        background: selected
                          ? "#fff"
                          : "transparent",
                      }}
                    />
                  </button>
                );
              })}
            </div>

            <div
              style={{
                height: 1,
                background:
                  "rgba(0,242,255,.18)",
                margin: "16px 0",
              }}
            />

            <div
              style={{
                color: "#c6d9e2",
                fontSize: 12,
                fontWeight: 850,
                marginBottom: 8,
              }}
            >
              📝 Précision supplémentaire{" "}
              <span
                style={{
                  color: "#718795",
                  fontWeight: 600,
                }}
              >
                {rejectReasonKey === "other"
                  ? "(obligatoire)"
                  : "(optionnel)"}
              </span>
            </div>

            <textarea
              value={rejectNote}
              maxLength={300}
              disabled={Boolean(workingId)}
              onChange={(e) => {
                setRejectNote(e.target.value);
                setRejectError("");
              }}
              placeholder={
                rejectReasonKey === "other"
                  ? "Écris le motif du refus..."
                  : "Ajoute une précision pour l'utilisateur si nécessaire..."
              }
              style={{
                boxSizing: "border-box",
                width: "100%",
                minHeight: 92,
                resize: "vertical",
                borderRadius: 13,
                border:
                  "1px solid rgba(100,150,180,.45)",
                background: "#06131f",
                color: "#fff",
                padding: 13,
                fontFamily: "inherit",
                fontSize: 12,
                outline: "none",
              }}
            />

            <div
              style={{
                textAlign: "right",
                color: "#6f8594",
                fontSize: 10,
                marginTop: 5,
              }}
            >
              {rejectNote.length}/300
            </div>

            {rejectError && (
              <div
                style={{
                  marginTop: 10,
                  borderRadius: 10,
                  padding: "9px 11px",
                  color: "#ff7891",
                  background:
                    "rgba(255,65,103,.08)",
                  border:
                    "1px solid rgba(255,65,103,.28)",
                  fontSize: 11,
                }}
              >
                ⚠️ {rejectError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 18,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                disabled={Boolean(workingId)}
                onClick={closeRejectModal}
                style={{
                  minWidth: 120,
                  padding: "11px 17px",
                  borderRadius: 11,
                  border:
                    "1px solid rgba(0,242,255,.35)",
                  background:
                    "rgba(0,242,255,.035)",
                  color: "#9cf8ff",
                  fontWeight: 900,
                  cursor: workingId
                    ? "default"
                    : "pointer",
                }}
              >
                Annuler
              </button>

              <button
                type="button"
                disabled={
                  Boolean(workingId) ||
                  !rejectReasonKey
                }
                onClick={confirmReject}
                style={{
                  minWidth: 190,
                  padding: "11px 17px",
                  borderRadius: 11,
                  border:
                    "1px solid rgba(255,80,110,.8)",
                  background:
                    "linear-gradient(180deg,#d92d52,#a91535)",
                  boxShadow:
                    "0 0 22px rgba(255,45,85,.18)",
                  color: "#fff",
                  fontWeight: 950,
                  cursor:
                    workingId || !rejectReasonKey
                      ? "default"
                      : "pointer",
                  opacity:
                    workingId || !rejectReasonKey
                      ? 0.5
                      : 1,
                }}
              >
                {workingId === rejectItem.id
                  ? "REFUS EN COURS..."
                  : "❌ CONFIRMER LE REFUS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const backButton = {
  background: "transparent",
  border: "1px solid rgba(0,242,255,.25)",
  color: "#00f2ff",
  borderRadius: 9,
  padding: "8px 12px",
  cursor: "pointer",
  fontWeight: 800,
};

const card = {
  background: "#061827",
  border: "1px solid rgba(0,242,255,.16)",
  borderRadius: 14,
  padding: 18,
  boxShadow: "0 8px 25px rgba(0,0,0,.22)",
};

const coverPlaceholder = {
  width: 64,
  height: 64,
  borderRadius: 12,
  background: "#0a2539",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 27,
};

const statusBox = {
  background: "rgba(0,0,0,.22)",
  border: "1px solid",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 12,
};

const actionButton = {
  border: "none",
  borderRadius: 9,
  color: "#fff",
  padding: "10px 16px",
  fontSize: 11,
  fontWeight: 900,
  cursor: "pointer",
};

const emptyBox = {
  background: "#061827",
  border: "1px solid rgba(0,242,255,.16)",
  borderRadius: 14,
  padding: 28,
  textAlign: "center",
  color: "#8095a3",
};

export default AdminMusicValidationPage;
